import { isMobileBrowser } from '../environment/utils';
function _getAdaptiveQualityConfig(state) {
    return state['features/base/config'].adaptiveQuality ?? {};
}
function _isWebConstrainedConnection(state) {
    const details = state['features/base/net-info'].details ?? {};
    const { downlink, effectiveType, saveData } = details;
    // Treat any effectiveType below 4G, explicit saveData flag, or a downlink
    // below 2 Mbps (covers degraded 4G / poor WiFi) as a constrained connection.
    return Boolean(saveData)
        || ['slow-2g', '2g', '3g'].includes(`${effectiveType ?? ''}`.toLowerCase())
        || (typeof downlink === 'number' && downlink > 0 && downlink < 2.0);
}
function _isNativeConstrainedConnection(state) {
    const netInfo = state['features/base/net-info'];
    const details = netInfo.details ?? {};
    const { cellularGeneration, isConnectionExpensive } = details;
    return Boolean(isConnectionExpensive)
        || netInfo.networkType === 'cellular'
        || ['2g', '3g'].includes(`${cellularGeneration ?? ''}`.toLowerCase());
}
export function getAdaptiveNetworkProfile(state) {
    const config = _getAdaptiveQualityConfig(state);
    const enabled = config.enabled !== false;
    const isMobileDevice = navigator.product === 'ReactNative' || isMobileBrowser();
    const isConstrainedNetwork = enabled
        && state['features/base/net-info'].isOnline !== false
        && (navigator.product === 'ReactNative'
            ? _isNativeConstrainedConnection(state)
            : _isWebConstrainedConnection(state));
    const profile = {
        enabled,
        forceAudioOnlyOnPoorNetwork: Boolean(config.forceAudioOnlyOnPoorNetwork),
        isConstrainedNetwork,
        isMobileDevice
    };
    if (!enabled) {
        return profile;
    }
    if (isMobileDevice) {
        profile.maxReceiveVideoQuality = config.mobilePreferredVideoQuality ?? 360;
        profile.maxSenderVideoQuality = config.mobileResolution ?? 540;
        profile.startLastN = config.mobileLastN ?? 6;
    }
    if (isConstrainedNetwork) {
        profile.maxFullResolutionParticipants = config.constrainedMaxFullResolutionParticipants ?? 1;
        profile.maxReceiveVideoQuality = Math.min(profile.maxReceiveVideoQuality ?? Infinity, config.constrainedPreferredVideoQuality ?? 180);
        profile.maxSenderVideoQuality = Math.min(profile.maxSenderVideoQuality ?? Infinity, config.constrainedResolution ?? 360);
        profile.prejoinTimeout = config.constrainedPrejoinTimeout ?? 20000;
        profile.screenSharingFrameRate = config.constrainedScreenSharingFrameRate ?? 4;
        profile.startLastN = Math.min(profile.startLastN ?? Infinity, config.constrainedLastN ?? 4);
    }
    if (profile.maxReceiveVideoQuality === Infinity) {
        delete profile.maxReceiveVideoQuality;
    }
    if (profile.maxSenderVideoQuality === Infinity) {
        delete profile.maxSenderVideoQuality;
    }
    if (profile.startLastN === Infinity) {
        delete profile.startLastN;
    }
    return profile;
}
export function getEffectiveMaxReceiverVideoQuality(state, preferredVideoQuality) {
    const { maxReceiveVideoQuality } = getAdaptiveNetworkProfile(state);
    return typeof maxReceiveVideoQuality === 'number'
        ? Math.min(preferredVideoQuality, maxReceiveVideoQuality)
        : preferredVideoQuality;
}
export function getEffectiveLastN(state, currentLastN) {
    const { startLastN } = getAdaptiveNetworkProfile(state);
    if (typeof startLastN !== 'number') {
        return currentLastN;
    }
    return currentLastN === -1 ? startLastN : Math.min(currentLastN, startLastN);
}
export function getEffectiveMaxFullResolutionParticipants(state, currentMaxFullResolutionParticipants = 2) {
    const { maxFullResolutionParticipants } = getAdaptiveNetworkProfile(state);
    return typeof maxFullResolutionParticipants === 'number'
        ? Math.min(currentMaxFullResolutionParticipants, maxFullResolutionParticipants)
        : currentMaxFullResolutionParticipants;
}
export function getEffectivePrejoinTimeout(state, fallbackTimeout) {
    const { prejoinTimeout } = getAdaptiveNetworkProfile(state);
    return prejoinTimeout ?? fallbackTimeout;
}
export function getEffectiveScreenSharingFrameRate(state, currentFrameRate) {
    const { screenSharingFrameRate } = getAdaptiveNetworkProfile(state);
    if (typeof currentFrameRate === 'number' && typeof screenSharingFrameRate === 'number') {
        return Math.min(currentFrameRate, screenSharingFrameRate);
    }
    return currentFrameRate ?? screenSharingFrameRate;
}
export function getEffectiveVideoCaptureSettings(state, { constraints, resolution }) {
    const { maxSenderVideoQuality } = getAdaptiveNetworkProfile(state);
    if (typeof maxSenderVideoQuality !== 'number') {
        return {
            constraints,
            resolution
        };
    }
    const effectiveResolution = typeof resolution === 'number'
        ? Math.min(resolution, maxSenderVideoQuality)
        : maxSenderVideoQuality;
    const nextConstraints = constraints ? {
        ...constraints,
        video: {
            ...constraints.video,
            height: {
                ...constraints.video?.height,
                ideal: Math.min(constraints.video?.height?.ideal ?? effectiveResolution, effectiveResolution),
                max: Math.min(constraints.video?.height?.max ?? effectiveResolution, effectiveResolution),
                min: constraints.video?.height?.min
            }
        }
    } : {
        video: {
            height: {
                ideal: effectiveResolution,
                max: effectiveResolution,
                min: 180
            }
        }
    };
    return {
        constraints: nextConstraints,
        resolution: effectiveResolution
    };
}
